<template>
  <div ref="$baseForm" v-editable="blok" class="base-form" :class="[blok.is_dark ? 'is-dark' : 'is-light']">
    <!-- Heading -->
    <div class="head-wrapper">
      <div class="head">
        <div class="head-content">
          <!-- Text -->
          <div v-if="isHeadText" class="text">
            <p class="statement-small">{{ blok.head_text }}</p>
          </div>

          <!-- Media Responsive -->
          <div v-if="isHeadMedia" class="media">
            <div class="media-wrapper">
              <UtilMediaResponsive
                v-if="!isMediaVideo || (isMediaVideo && isMediaWebm)"
                :image="blok.head_media"
                :video="blok.head_media"
                :autoplay="true"
                aspectRatio="3/2" />
            </div>
          </div>

          <!-- Contact Box — Desktop -->
          <BaseFormContactBox v-if="blok.component === 'fm_contact'" :blok="blok" class="is-desktop" />
        </div>
      </div>
    </div>

    <!-- Form -->
    <div class="form-container">
      <slot name="form"></slot>
    </div>

    <BaseFormContactBox v-if="blok.component === 'fm_contact'" :blok="blok" class="is-mobile" />

    <!-- Success Modal -->
    <BaseFormSuccessModal ref="$baseFormSuccessModal" :blok="blok" />
  </div>
</template>

<script lang="ts" setup>
import { gsap } from 'gsap';
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useScroll, useTransition } from '@seb-platform/shared/composables';
import { isVideo, isVideoWebm } from '@seb-platform/shared/utils/media';
import UtilMediaResponsive from '~/components/storyblok/utils/UtilMediaResponsive/UtilMediaResponsive.vue';
import type { FmContactStoryblok, FmNewsletterStoryblok } from '~/types/storyblok-generated';
import BaseFormSuccessModal from '../BaseFormSuccessModal/BaseFormSuccessModal.vue';
import BaseFormContactBox from './BaseFormContactBox.vue';

// TODO this component is currently not in use -- FMNewsletter should have it's own layout just like FMContact and this component should be removed

const props = defineProps<{ blok: FmContactStoryblok | FmNewsletterStoryblok }>();

defineExpose({ openSuccessModal });

const { state: scrollState } = useScroll();
const { checkViewPortElements, removeViewPortElements, addViewPortElements, hideElements } = useTransition();

const $baseForm = ref<HTMLElement>();
const $baseFormSuccessModal = ref<InstanceType<typeof BaseFormSuccessModal>>();

const isHeadText = computed(() => props.blok.head_type === 'text' && !!props.blok.head_text?.length);
const isHeadMedia = computed(() => props.blok.head_type === 'media' && !!props.blok.head_media?.filename);
const isMediaVideo = computed(() => isHeadMedia.value && isVideo(props.blok.head_media?.filename));
const isMediaWebm = computed(() => isHeadMedia.value && isVideoWebm(props.blok.head_media?.filename));

function openSuccessModal() {
  $baseFormSuccessModal.value?.openModal();
}

watch(() => scrollState.top, watchPageScroll);

function watchPageScroll() {
  if (!$baseForm.value) {
    return;
  }
  checkViewPortElements($baseForm.value);
}

function initAnimations() {
  if (!$baseForm.value) {
    return;
  }
  addViewPortElements($baseForm.value.querySelectorAll('.form-container'));
  addViewPortElements($baseForm.value.querySelectorAll('.contact-box'));
  addViewPortElements($baseForm.value.querySelectorAll('.media'));

  void nextTick(() => {
    if (!$baseForm.value) {
      return;
    }
    hideElements($baseForm.value);
    gsap.set($baseForm.value, { autoAlpha: 1 });
    checkViewPortElements($baseForm.value);
  });
}

onMounted(() => {
  initAnimations();
});

onBeforeUnmount(() => {
  if (!$baseForm.value) {
    return;
  }
  removeViewPortElements($baseForm.value);
});
</script>

<style src="./BaseForm.scss" lang="scss" scoped />
